/**
 * トップページ（LP）の掲載内容。
 * 訴求はワーカー向け LP（start.care-link.website）から移植している。
 * LP と共通の数値を更新するときは、LP 側と同時にこの定数も更新する。
 */

/**
 * ブランドカラー。白文字とのコントラスト比 6.2:1（WCAG AA）を満たす。
 * CSS 側（Home.module.css）の同じ色はこの値に合わせて更新する。
 */
export const HOME_BRAND_COLOR = "#0F6E56";

/** LP のヒーローに表示している登録者数の表記。LP を更新するときは同時に更新する */
const HOME_REGISTERED_WORKER_LABEL = "18,000人突破";

/** 募集エリア。LP の首都圏版・関西版に記載しているエリアと一致させる */
export const HOME_SERVICE_AREAS = [
  {
    region: "関東",
    prefectures: ["東京都", "千葉県", "埼玉県", "神奈川県", "茨城県"],
  },
  { region: "関西", prefectures: ["大阪府", "兵庫県", "京都府"] },
] as const;

/** ヒーローに並べるサービスの強み。業務種別に依存する金額は載せない */
export const HOME_HERO_STATS = [
  { label: "登録者数", value: HOME_REGISTERED_WORKER_LABEL },
  { label: "面接", value: "面接なし" },
  { label: "報酬の受け取り", value: "最短当日" },
] as const;

export const HOME_FEATURES = [
  {
    image: "/images/top/feature01.webp",
    title: "1時間から。半日でも。あなたのペースで",
    description:
      "家事の合間でも、本業の休日でも。1時間単位で働ける求人だから、無理なく自分のペースで続けられます。",
  },
  {
    image: "/images/top/feature02.webp",
    title: "面接ゼロ・履歴書不要。スマホだけで完結",
    description:
      "履歴書も登録会も不要。スマホで1分の登録から、求人への応募・確定まで全てオンラインで完結します。",
  },
  {
    image: "/images/top/feature03.webp",
    title: "報酬を最短当日に受け取れる",
    description:
      "最短で勤務当日に給与振込。振込申請などの手続きは不要で、働いた分がそのまま振り込まれます。",
  },
  {
    image: "/images/top/feature04.webp",
    title: "現場を、就職前に体験できる",
    description:
      "就職や転職を考えている方も、実際の現場で「自分に合うか」を確かめてから判断できます。",
  },
] as const;

/**
 * 求人サンプル。実際の求人データは参照せず、固定の例示として表示する。
 * 職種は募集実績のあるものに限る（福祉3件は LP から移植、介護2件・看護1件は実績上位の職種）。
 *
 * 金額と勤務時間は、募集エリアで最も高い最低賃金（東京都）を、深夜割増・時間外割増を
 * 除いた基礎時給で上回るように決めている。最低賃金が改定されたら下回らないか見直す。
 */
export const HOME_JOB_SAMPLES = [
  {
    image: "/images/top/sample01.webp",
    width: 692,
    height: 392,
    title: "障害者グループホーム夜勤",
    tags: ["グループホーム", "夜勤", "未経験OK"],
    workTime: "夜勤　17:00-9:00（休憩・仮眠5時間）",
    wage: "日給　18,000円",
  },
  {
    image: "/images/top/sample02.webp",
    width: 692,
    height: 392,
    title: "放課後等デイサービス",
    tags: ["放課後デイ", "日勤", "未経験OK"],
    workTime: "日勤　13:00-17:30",
    wage: "日給　6,000円",
  },
  {
    image: "/images/top/sample03.webp",
    width: 692,
    height: 392,
    title: "就労継続支援B型サポート",
    tags: ["就労支援", "駅近", "休憩あり"],
    workTime: "日勤　9:00-17:00（休憩60分）",
    wage: "日給　10,000円",
  },
  {
    image: "/images/top/feature04.webp",
    width: 1036,
    height: 640,
    title: "住宅型有料老人ホームの夜勤",
    tags: ["有料老人ホーム", "夜勤", "未経験OK"],
    workTime: "夜勤　17:00-9:00（休憩・仮眠4時間）",
    wage: "日給　24,000円",
  },
  {
    image: "/images/top/feature01.webp",
    width: 1036,
    height: 640,
    title: "デイサービスの介護スタッフ",
    tags: ["デイサービス", "日勤", "未経験OK"],
    workTime: "日勤　8:30-17:30（休憩60分）",
    wage: "日給　10,500円",
  },
  {
    image: "/images/top/work06.webp",
    width: 692,
    height: 400,
    title: "訪問看護の夜勤",
    tags: ["訪問看護", "夜勤", "看護師・准看護師の資格が必要"],
    workTime: "夜勤　17:00-9:00（休憩・仮眠4時間）",
    wage: "日給　30,000円",
  },
] as const;

/** 求人サンプルが例示であることの注記 */
export const HOME_JOB_SAMPLE_NOTE =
  "＊掲載している条件は例です。実際の募集条件は求人ごとに異なります。金額は休憩時間を除いた実働分の目安です。";

/** お仕事内容例。資格が要る看護業務は含めず、ワーカーが担う補助業務の例を並べる */
export const HOME_JOB_TASKS = [
  {
    image: "/images/top/work01.webp",
    width: 692,
    height: 400,
    title: "見守り・声かけ",
    description: "利用者様の安全を見守り、必要に応じて声かけを行います。",
  },
  {
    image: "/images/top/work02.webp",
    width: 692,
    height: 400,
    title: "生活支援",
    description: "食事・入浴・着替えなど日常生活のサポートを行います。",
  },
  {
    image: "/images/top/work03.webp",
    width: 692,
    height: 400,
    title: "送迎補助",
    description: "利用者様のご自宅から施設への送迎をお手伝いします。",
  },
  {
    image: "/images/top/work04.webp",
    width: 692,
    height: 400,
    title: "レクリエーション",
    description: "利用者様が楽しめる活動の企画・運営をサポートします。",
  },
  {
    image: "/images/top/work05.webp",
    width: 692,
    height: 400,
    title: "就労支援サポート",
    description: "作業所での軽作業や就労訓練のサポートを行います。",
  },
  {
    image: "/images/top/work06.webp",
    width: 692,
    height: 400,
    title: "外出支援",
    description: "買い物や散歩など外出時の付き添いを行います。",
  },
] as const;
